import { Text, useSitecoreContext, LayoutServicePageState, Placeholder } from '@sitecore-jss/sitecore-jss-nextjs';
import { useI18n } from 'next-localization';
import classNames from 'classnames/bind';
import React, { useEffect, useState } from 'react';

import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';

import styles from './PlanDetails.module.scss';
import mockData from '../MockData/mock.data.plan-details.json';
import { dataFetcher } from 'lib/data-fetcher';

import { useRouter } from 'next/router';
import { PlanResponse, PlanDetailEntry } from '../Types/GetPlanDetailsResponse.Type';
import Button from 'components/helpers/Button/Button';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';
import Loader, { LoaderVariant } from 'components/helpers/Loader/Loader';
import PlanDocuments from './PlanDocuments/PlanDocuments';

import { getQueryStringParameter, isValidSitecoreItemGUID } from 'lib/utils';
import PlanDetailGroupEntries from './PlanDetailGroupEntries/PlanDetailGroupEntries';
import PrescriptionDrugCoverage from './PrescriptionDrugCoverage/PrescriptionDrugCoverage';

import { PlanDetailsProps } from './PlanDetails.Types';
import {
  buildApplyToEnrollURL,
  checkEnrollEnable,
  getMatchingDrugCoverageGroupOption,
} from 'components/PlanData/utilities';
import { PlanYear } from '../Types/PlanYear.Type';
import { UsedOption } from '../Types/DrugCoverageTables.Type';

const cx = classNames.bind(styles);

const getStarRatingAriaLabel = (label: string, ratingValue: string | number) => {
  return label.replace('[rating]', ratingValue.toString());
};

const getPlanId = (plan: PlanResponse, index = 0, prefix = '') => {
  let $id = `${prefix}${plan?.planName?.value}-${index}`;
  $id = $id.replaceAll(' ', '');
  $id = $id.replaceAll('(', '');
  $id = $id.replaceAll(')', '');
  $id = $id.replaceAll('$', '');

  return $id;
};

const PlanDetails = (props: PlanDetailsProps): React.JSX.Element => {
  const { className, fields, rendering } = props;
  const [plans, setPlans] = useState<PlanResponse[]>([]);
  const [enableEnroll, setEnableEnroll] = useState(false);
  const router = useRouter();
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();
  const [drugCoverageGroup, setDrugCoverageGroup] = useState<UsedOption>();
  const includeDocFields = fields.showPlanDocuments;
  const includeBenefitsFields = fields.showAdditionalBenefits;
  const [showNoResultsFound, setShowNoResultsFound] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(true);

  useEffect(() => {
    let planItemId = '';
    let allParamsFound = false;

    if (sitecoreContext.pageState === LayoutServicePageState.Normal) {
      if (router.isReady) {
        if (router.asPath?.indexOf('?') > 0) {
          const planGUID = getQueryStringParameter('planId', router.query);
          const planYear = getQueryStringParameter('year', router.query);

          if (isValidSitecoreItemGUID(planGUID.trim())) {
            planItemId = planGUID;
          }

          const allTempYears: PlanYear[] = [];
          fields.planYears.forEach((thisYear) => {
            allTempYears.push({
              id: '',
              year: thisYear.year,
              enrollButtonStart: thisYear.enrollButtonStart,
              displayEnd: new Date(),
              displayStart: new Date(),
            });
          });
          if (planItemId && planYear) {
            setEnableEnroll(checkEnrollEnable(allTempYears, parseInt(planYear)));
            if (fields.planType) {
              allParamsFound = true;
            }
          }
        }

        if (!allParamsFound) {
          router.push(fields.searchpageurl);
        }
      }

      const baseUrl = process.env['PUBLIC_URL'] || '';
      if (allParamsFound) {
        dataFetcher<PlanResponse[]>(`${baseUrl}/api/getPlanDetails`, {
          planTypeId: fields.planType?.id,
          planItemId: planItemId,
          includeDocFields: includeDocFields,
          includeBenefitsFields: includeBenefitsFields,
          language: sitecoreContext.language,
        }).then((response) => {
          const $data = response.data.filter((d) => d.planName.value);

          setPlans([...new Set($data)]);
          if ($data.length > 0 && $data[0].prescriptionDrugTables) {
            setDrugCoverageGroup(
              getMatchingDrugCoverageGroupOption(
                fields.drugCoverageTableGroups,
                $data[0].prescriptionDrugTables.targetId
              )
            );
          }
          setShowLoader(false);
          setShowNoResultsFound($data.length === 0);
        });
      }
    } else {
      setPlans(mockData as unknown as PlanResponse[]);
      setEnableEnroll(true);
      setShowLoader(false);
      setShowNoResultsFound((mockData as unknown as PlanResponse[]).length === 0);
    }
  }, [fields, includeBenefitsFields, includeDocFields, router, setPlans, sitecoreContext]);

  const handleApplyToEnroll = async () => {
    window.location.href = await buildApplyToEnrollURL({
      planTypeName: fields.planType.name,
      zipcode: getQueryStringParameter('zipcode', router.query),
      county: getQueryStringParameter('county', router.query),
      year: getQueryStringParameter('year', router.query),
      planName: plans[0].planName.value,
      contract: plans[0].contract.value,
      pbp: plans[0].pbp.value,
      groupID: plans[0].groupId.value,
      crmPrefix: fields.crmPrefix,
      mcProductID: plans[0].mcproductid.value?.toString(),
      cta: 'applytoenroll',
    });
    return false;
  };

  const drugCoveragePlanDetailEntries: PlanDetailEntry[] = [];
  const nonDrugPlanDetailEntries: PlanDetailEntry[] = [];

  return (
    <section className={cx('plan-details-listing', className)}>
      {showLoader && (
        <div role="status">
          <span className={cx('screen-reader-only')}>{t('Plan details loaded')}</span>
          <Loader variant={LoaderVariant.Spinner} />
        </div>
      )}

      {!showLoader && showNoResultsFound && (
        <div role="status">
          <strong className={cx('no-plan')}>{t('MedicareShopNoPlan')}</strong>
        </div>
      )}
      {!showLoader && !showNoResultsFound && plans?.length > 0 && (
        <>
          {plans.map((plan, index) => (
            <React.Fragment key={index}>
              <section className={cx('plan-header')}>
                <Text tag="h1" encode={sitecoreContext.pageEditing} field={plan.planName} />
                {plan.starRating?.targetItem?.value?.value && (
                  <div
                    className={cx('star-icon')}
                    aria-label={getStarRatingAriaLabel(t('Aria-Start-Rating'), plan.starRating.targetItem.value?.value)}
                    role={'img'}
                  >
                    {Array.from({ length: Number(plan.starRating.targetItem.value?.value) }).map((_, starIndex) => (
                      <img
                        key={`${plan.planName}-star-${starIndex}`}
                        src="/static-assets/mc-shop/star-light-bg.svg"
                        alt="Star Rating"
                      />
                    ))}
                    {Number(plan.starRating.targetItem.value?.value) % 1 !== 0 && (
                      <img
                        src="/static-assets/mc-shop/star-light-bg.svg"
                        alt="Half Star Rating"
                        className={cx('half-star-icon')}
                      />
                    )}
                  </div>
                )}
                <RichTextWrapper field={plan.planDescription} className={cx('plan-header-paragraph')} />
                {enableEnroll && (
                  <Button
                    className="mt-3 capitalize"
                    id={getPlanId(plan, index, 'enroll-')}
                    onClick={handleApplyToEnroll}
                    type="button"
                  >
                    {t('Apply to Enroll')}
                  </Button>
                )}
              </section>
              <section className={cx('plan-details-listing__plan')}>
                {plan.planDetailEntries &&
                  plan.planDetailEntries.targetItems.length > 0 &&
                  (() => {
                    plan.planDetailEntries?.targetItems?.forEach((item) => {
                      const caption = item.entryRow.entryTable?.caption?.value;

                      if (caption === 'Additional Drug Coverage') {
                        drugCoveragePlanDetailEntries.push(item);
                      } else {
                        nonDrugPlanDetailEntries.push(item);
                      }
                    });
                    return (
                      <PlanDetailGroupEntries
                        planDetailEntries={nonDrugPlanDetailEntries}
                        planDetailGroupEntryOrder={props.fields.planDetailGroupOrder}
                        planDetailRowsOrder={props.fields.planDetailRowsOrder}
                      />
                    );
                  })()}
                {drugCoverageGroup && drugCoverageGroup.drugCoverageTables.length > 0 && (
                  <PrescriptionDrugCoverage prescriptionDrugTables={drugCoverageGroup} />
                )}
                {drugCoveragePlanDetailEntries.length > 0 && (
                  <PlanDetailGroupEntries
                    planDetailEntries={drugCoveragePlanDetailEntries}
                    planDetailGroupEntryOrder={[]}
                    planDetailRowsOrder={[]}
                  />
                )}
                {plan.additionalBenefits?.targetItem && (
                  <section>
                    <Text
                      tag="h2"
                      className={cx('section-header', 'container-edge')}
                      encode={sitecoreContext.pageEditing}
                      field={plan.additionalBenefits.targetItem.caption}
                    />
                    <RichTextWrapper field={plan.additionalBenefits.targetItem.value} />
                  </section>
                )}
                {plan.documents && plan.documents.targetItems.length > 0 && (
                  <PlanDocuments documents={plan.documents.targetItems} />
                )}
              </section>
            </React.Fragment>
          ))}
        </>
      )}
      <section className={cx('plan-details-listing__plan')}>
        <Placeholder key={0} name="hp-container-3070-left" rendering={rendering} disableSuspense={true} />
      </section>
      <aside className={cx('plan-details-listing__aside')}>
        <Placeholder key={0} name="hp-container-3070-right" rendering={rendering} disableSuspense={true} />
      </aside>
    </section>
  );
};

export default compose<PlanDetailsProps>(withStyles())(PlanDetails);
